import { CheckCircleOutline, Clear, MoreHoriz, Refresh } from '@mui/icons-material'
import { Box, Chip, CircularProgress, Tooltip, Typography } from '@mui/material'
import React from 'react'

type Status = 'processing' | 'success' | 'failure' | 'pending'

export interface ChatflowFileChipProps {
  id?: string
  name?: string
  status: Status
  variant?: 'grey' | 'dark-grey'
  tooltip?: React.ReactNode
  onDelete?: () => void
  onRetry?: () => void
}

export default function ChatflowFileChip({ name, status, variant = 'grey', tooltip, onDelete, onRetry }: ChatflowFileChipProps) {
  return (
    <Chip
      label={<ChipLabel name={name} status={status} tooltip={tooltip} onRetry={onRetry} />}
      sx={{
        borderRadius: '8px',
        display: 'flex',
        gap: '0px',
        px: '4px',
        background: t => (variant === 'grey' ? t.colors.base[60] : t.colors.base[50]),
      }}
      onDelete={onDelete}
      deleteIcon={<Clear sx={{ height: '16px', width: '16px', color: '#fff !important' }} />}
    />
  )
}

function ChipLabel({ name, status, tooltip, onRetry }: Omit<ChatflowFileChipProps, 'onDelete' | 'id'>) {
  const statusComponent: Record<Status, React.ReactElement | undefined> = {
    processing: <CircularProgress sx={{ color: '#fff' }} size="12px" />,
    success: <CheckCircleOutline sx={{ color: '#00A91C', height: '16px', width: '16px' }} />,
    failure: (
      <Refresh
        sx={{
          color: t => `${t.palette.global.primaryRed}`,
          height: '16px',
          width: '16px',
        }}
      />
    ),
    pending: <MoreHoriz sx={{ color: t => t.colors.base[45], height: '16px', width: '16px' }} />,
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography>{name}</Typography>
      <Tooltip sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={tooltip}>
        <Box onClick={onRetry}>{statusComponent[status]}</Box>
      </Tooltip>
    </Box>
  )
}
